import { useMemo, useState } from 'react';

import { notify } from '../../../app';
import {
  useAuth,
  useFetch,
  useListFetch,
  useModal,
  useRequest,
} from '../../../hooks';
import { useUrlShortener } from '../../url-sortener/hooks/useUrlShortener';
import {
  addScrapingTask,
  getScrapingList,
  downloadScrapingTask,
  retryScrapingTask,
  cancelScrapingTask,
  getShortAgents,
  bulkScrapingTasksExport,
  deleteScrapingTasks,
} from '../../../api';
import { FileTypes } from '../../../services/scraper';
import type { TaskDetails } from '../../../services/scraper';
import { downloadCsvFile, convertTasksDetailsToCsv } from './utils';
import type { FormProps } from './components/TaskForm';
import type { TaskPayload, TaskStatusValue } from '../../../api';
import type { CompanyInfo } from './components/BulkUploadPreview';
import { normalizePageLimitValue } from './utils/validators';
import { isSuccessStatus, ScrapingStatus } from './const';

const processStringValue = (string: string): string | undefined => {
  if (typeof string !== 'string') return string;
  return string?.length ? string : undefined;
};

const MAX_DEPTH = 6;

const defaultFormValues: FormProps = {
  is_include_links: true,
  depth: 3,
  take_screenshot: true,
  url: '',
  hard_parse: false,
  companies_info: [],
  should_create_demo_bot: false,
  page_limit: 250,
};

export const useScraping = () => {
  const { user } = useAuth();
  const { isOpened, onClose, onOpen } = useModal();
  const {
    isOpened: isDeleteModalOpen,
    onClose: onCloseDeleteModal,
    onOpen: onOpenDeleteModal,
  } = useModal();
  const {
    isOpened: isBulkDeleteModalOpen,
    onClose: onCloseBulkDeleteModal,
    onOpen: onOpenBulkDeleteModal,
  } = useModal();
  const {
    isOpened: isInfoModalOpen,
    onClose: onCloseInfoModal,
    onOpen: onOpenInfoModal,
  } = useModal();
  const {
    isOpened: isErrorModalOpen,
    onClose: onCloseErrorModal,
    onOpen: onOpenErrorModal,
  } = useModal();

  const { data: agentsOptions } = useFetch(async () => {
    const data = await getShortAgents();

    return (data ?? []).map(d => ({
      value: d.id ?? '',
      label: d.title,
    }));
  }, []);

  const [currentForm, setCurrentForm] = useState<
    Partial<TaskPayload> | undefined
  >(undefined);

  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskDetails | null>(null);
  const [errorTask, setErrorTask] = useState<TaskDetails | null>(null);

  const { visitStats, isLoadingStats } = useUrlShortener(
    selectedTask?.demo_url || null,
    isInfoModalOpen
  );

  const [formId, setFormId] = useState(1);
  const [formValues, setFormValues] = useState<FormProps>(defaultFormValues);
  const [bulkUploadData, setBulkUploadData] = useState<CompanyInfo[]>([]);
  const [formKey, setFormKey] = useState(1);

  const handleClose = () => {
    onClose();
    setCurrentForm(undefined);
    resetFormState();
  };

  const handleFormChange = (newValues: FormProps) => {
    setFormValues(prev => ({ ...prev, ...newValues }));
  };

  const handleBulkUpload = (rows: Record<string, string>[] | null) => {
    if (!rows) {
      setBulkUploadData([]);
      return;
    }

    const companies = rows
      .map(row => ({
        company_name: row.company_name,
        url: row.url,
      }))
      .filter(company => company.company_name && company.url);

    setBulkUploadData(companies);
  };

  const handleClearBulkUpload = () => {
    setBulkUploadData([]);
  };

  const resetFormState = () => {
    setFormValues(defaultFormValues);
    setBulkUploadData([]);
    setFormKey(k => k + 1);
  };

  const handleSelectTask = (taskId: string, taskStatus: string) => {
    if (!isSuccessStatus(taskStatus)) {
      return;
    }

    setIsSelectAllMode(false);
    setSelectedTaskIds(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = (tasks: any[]) => {
    const successTaskIds = tasks
      .filter(task => isSuccessStatus(task.status))
      .map(task => task.id)
      .filter(Boolean);

    if (selectedTaskIds.length === successTaskIds.length) {
      setSelectedTaskIds([]);
      setIsSelectAllMode(false);
    } else {
      setSelectedTaskIds(successTaskIds);
      setIsSelectAllMode(true);
    }
  };

  const clearSelection = () => {
    setSelectedTaskIds([]);
    setIsSelectAllMode(false);
  };

  const {
    nodes: scrapingList = [],
    intializing: loadingScraping,
    loading: reloadingScraping,
    refresh,
    filters,
    setFilters,
    ...rest
  } = useListFetch(
    params => {
      const statusFilter = processStringValue(params?.filters?.status);
      const mappedStatus =
        statusFilter === ScrapingStatus.IN_PROCESS
          ? (ScrapingStatus.IN_PROCESS as unknown as TaskStatusValue)
          : (statusFilter as TaskStatusValue) || null;

      return getScrapingList(
        (params?.pagination?.page ?? 1) + 1,
        params?.pagination?.perPage ?? 10,
        processStringValue(params?.filters?.url) || null,
        processStringValue(params?.filters?.campaign_name) || null,
        mappedStatus
      );
    },
    {
      deps: [],
      withQueryParams: true,
    }
  );

  const taskData = useMemo((): FormProps => {
    const depthValue = formValues.depth === MAX_DEPTH ? null : formValues.depth;
    const normalizedPageLimit = normalizePageLimitValue(formValues.page_limit);

    return {
      url: bulkUploadData.length > 0 ? '' : formValues.url,
      depth: depthValue,
      is_include_links: formValues.is_include_links,
      take_screenshot: formValues.take_screenshot,
      hard_parse: formValues.hard_parse,
      organisation_name:
        bulkUploadData.length > 0 ? '' : formValues.organisation_name,
      agent_id: formValues.agent_id,
      include_path_regex: formValues.include_path_regex,
      exclude_path_regex: formValues.exclude_path_regex,
      campaign_name: formValues.campaign_name,
      should_create_demo_bot: formValues.should_create_demo_bot,
      companies_info: bulkUploadData.length > 0 ? bulkUploadData : undefined,
      page_limit: normalizedPageLimit,
    };
  }, [formValues, bulkUploadData]);

  const [handleAddScrapingTask, addingScraping] = useRequest(
    async _signal => {
      const {
        agent_id,
        campaign_name,
        depth,
        take_screenshot,
        is_include_links,
        include_path_regex,
        exclude_path_regex,
        hard_parse,
        url,
        organisation_name,
        should_create_demo_bot,
        companies_info,
        page_limit,
      } = taskData;

      let finalCompaniesInfo: CompanyInfo[] = [];

      if (companies_info && companies_info.length > 0) {
        finalCompaniesInfo = companies_info;
      } else if (organisation_name && url) {
        finalCompaniesInfo = [
          {
            company_name: organisation_name,
            url,
          },
        ];
      } else if (!should_create_demo_bot) {
        finalCompaniesInfo = [
          {
            company_name: '',
            url,
          },
        ];
      } else {
        throw new Error(
          'Either upload a file with companies or provide Company Name and URL'
        );
      }

      const payload = {
        companies_info: finalCompaniesInfo,
        agent_id,
        campaign_name,
        depth,
        include_path_regex,
        exclude_path_regex,
        requested_by: user?.name,
        take_screenshot,
        page_limit: page_limit ?? 250,
        is_include_links,
        hard_parse,
        should_create_demo_bot,
      };

      return addScrapingTask(payload as TaskPayload);
    },
    {
      onSuccess: () => {
        refresh();
        onClose?.();
        notify.success('New Scraping task created');
        resetFormState();
      },
      onError: error => {
        const errorMessage =
          error?.message || 'Failed to create new Scraping task';
        notify.error(errorMessage);
      },
      deps: [formKey, formValues, bulkUploadData],
    }
  );

  const [handleretryScrapingTask, retringScrapingTask] = useRequest(
    (_signal, taskId: string) => retryScrapingTask(taskId),
    {
      onSuccess: () => {
        refresh();
        onClose?.();
        notify.success('Scraping task recreated successfully');
      },
      onError: () => {
        notify.error('Failed to recreate Scraping task');
      },
      deps: [],
    }
  );

  const [handleCancelScrapingTask, cancelingScrapingTask] = useRequest(
    (_signal, taskId: string) => cancelScrapingTask(taskId),
    {
      onSuccess: () => {
        refresh();
        onClose?.();
        notify.success('Scraping task camceled successfully');
      },
      onError: () => {
        notify.error('Failed to cancel task');
      },
      deps: [],
    }
  );

  const [handleDownloadScrapingTask, downloadingScraping] = useRequest(
    async (_signal, taskId: string, fileType: FileTypes = FileTypes.JSON) => {
      const blob = await downloadScrapingTask(taskId, fileType);

      if (!(blob instanceof Blob)) {
        notify.error('Invalid response received.');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = fileType === FileTypes.JSON ? 'json' : 'zip';
      a.download = `scraping-task-${Date.now()}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      notify.success('Scraping data downloaded successfully');
    },
    {
      onError: error => {
        notify.error('Failed to download Scraping data');
      },
      deps: [],
    }
  );

  const [handleBulkScrapingTasksExport, bulkScrapingExport] = useRequest(
    async _signal => {
      const urlFilter = processStringValue(filters?.url) || null;
      const campaignFilter = processStringValue(filters?.campaign_name) || null;
      const taskIdsToExport = isSelectAllMode ? null : selectedTaskIds;

      return await bulkScrapingTasksExport(
        taskIdsToExport,
        urlFilter,
        campaignFilter
      );
    },
    {
      onSuccess: response => {
        try {
          let csvContent: string;

          if (response instanceof Blob) {
            response.text().then(text => {
              csvContent = text;
              downloadCsvFile(csvContent, selectedTaskIds, clearSelection);
            });
            return;
          } else if (typeof response === 'string') {
            csvContent = response;
          } else {
            csvContent = convertTasksDetailsToCsv(response);
          }

          downloadCsvFile(csvContent, selectedTaskIds, clearSelection);
        } catch (error) {
          notify.error('Failed to process scraping tasks data');
        }
      },
      onError: error => {
        console.error('Bulk download error:', error);
        notify.error('Failed to download scraping tasks');
      },
      deps: [selectedTaskIds, filters, isSelectAllMode],
    }
  );

  const [handleDeleteScrapingTasks, deletingScraping] = useRequest(
    async (_signal, taskIds: string[]) => {
      await deleteScrapingTasks(taskIds);
    },
    {
      onSuccess: () => {
        refresh();
        clearSelection();
        setDeleteTaskId(null);
        onCloseDeleteModal();
        onCloseBulkDeleteModal();
        notify.success('Scraping tasks deleted successfully');
      },
      onError: error => {
        const errorMessage =
          error?.message || 'Failed to delete scraping tasks';
        notify.error(errorMessage);
      },
      deps: [],
    }
  );

  const handleDeleteClick = (taskId: string) => {
    setDeleteTaskId(taskId);
    onOpenDeleteModal();
  };

  const handleConfirmDelete = () => {
    if (deleteTaskId) {
      handleDeleteScrapingTasks([deleteTaskId]);
      setDeleteTaskId(null);
      onCloseDeleteModal();
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteTaskId(null);
    onCloseDeleteModal();
  };

  const handleInfoClick = (task: TaskDetails) => {
    setSelectedTask(task);
    onOpenInfoModal();
  };

  const handleCloseInfoModal = () => {
    setSelectedTask(null);
    onCloseInfoModal();
  };

  const handleErrorClick = (task: TaskDetails) => {
    setErrorTask(task);
    onOpenErrorModal();
  };

  const handleCloseErrorModal = () => {
    setErrorTask(null);
    onCloseErrorModal();
  };

  const handleBulkDeleteClick = () => {
    onOpenBulkDeleteModal();
  };

  const handleConfirmBulkDelete = () => {
    if (selectedTaskIds.length > 0) {
      handleDeleteScrapingTasks(selectedTaskIds);
      onCloseBulkDeleteModal();
    }
  };

  const handleCloseBulkDeleteModal = () => {
    onCloseBulkDeleteModal();
  };

  return {
    currentForm,
    currentFormId: 'scraping',
    setCurrentForm,
    handleCancelScrapingTask,
    cancelingScrapingTask,
    scrapingList: scrapingList || [],
    loading: loadingScraping || reloadingScraping || retringScrapingTask,
    updating: addingScraping,
    downloading: downloadingScraping,
    selectedTaskIds,
    handleSelectTask,
    handleSelectAll,
    clearSelection,
    handleBulkScrapingTasksExport,
    bulkScrapingExport,
    handleDeleteScrapingTasks,
    deletingScraping,
    isOpened,
    onOpen,
    refresh,
    handleAddScrapingTask,
    handleDownloadScrapingTask,
    handleretryScrapingTask,
    onClose: handleClose,
    agentsOptions: agentsOptions || [],
    formId: String(formId),
    setFormId,
    formKey: String(formKey),
    formValues,
    setFormValues: handleFormChange,
    bulkUploadData,
    handleBulkUpload,
    handleClearBulkUpload,
    resetFormState,
    filters,
    setFilters,
    deleteTaskId,
    isDeleteModalOpen,
    handleDeleteClick,
    handleConfirmDelete,
    handleCloseDeleteModal,
    isBulkDeleteModalOpen,
    handleBulkDeleteClick,
    handleConfirmBulkDelete,
    handleCloseBulkDeleteModal,
    selectedTask,
    isInfoModalOpen,
    handleInfoClick,
    handleCloseInfoModal,
    visitStats,
    isLoadingStats,
    errorTask,
    isErrorModalOpen,
    handleErrorClick,
    handleCloseErrorModal,
    ...rest,
  };
};
